import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Volume2, 
  Music, 
  Store, 
  Settings, 
  Play, 
  Pause, 
  Download,
  Trash2,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Archive,
  FileText,
  Info,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import ProductAudioManager from './ProductAudioManager';
import VoiceSettings from './VoiceSettings';
import SubscriptionWidget from './SubscriptionWidget';
import BillingPlans from './BillingPlans';
import AppEmbedOnboarding from './AppEmbedOnboarding';
import AnalyticsDashboard from './AnalyticsDashboard';
import TrialBanner from './TrialBanner';

const Dashboard = ({ shopDomain, onLogout, isEmbedded = false }) => {
  const [products, setProducts] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBilling, setShowBilling] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [features, setFeatures] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [stats, setStats] = useState({
    totalProducts: 0,
    productsWithAudio: 0,
    totalAudios: 0,
    activeProducts: 0,
    draftProducts: 0,
    archivedProducts: 0
  });

  useEffect(() => {
    loadDashboardData();
    loadSyncStatus();
    loadSubscription();
    loadTrialStatus();
    apiClient.get('/features').then(r => {
      setFeatures(r.data.features || {});
    }).catch(() => {});
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await apiClient.get('/billing/subscription');
      // API returns { subscription: {...}, usage: {...} }
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
      // Default to free if no subscription
      setSubscription({ plan_name: 'free' });
    }
  };

  const loadTrialStatus = async () => {
    try {
      const response = await apiClient.get('/trial/status');
      setTrialStatus(response.data);
    } catch (error) {
      console.error('Error loading trial status:', error);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const res = await apiClient.get('/products/sync/status');
      setSyncStatus(res.data);
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const loadDashboardData = async (page = 1) => {
    setLoading(true);
    try {
      const [productsRes, voicesRes, syncStatusRes] = await Promise.all([
        apiClient.get(`/products?page=${page}&limit=50`),
        apiClient.get('/voices'),
        apiClient.get('/products/sync/status')
      ]);

      const productList = productsRes.data.products || [];
      setProducts(productList);
      setVoices(voicesRes.data.voices || []);
      setSyncStatus(syncStatusRes.data);
      
      if (productsRes.data.pagination) {
        setPagination(productsRes.data.pagination);
      }

      // Calculate stats from pagination data or product list
      const totalProducts = productsRes.data.pagination?.total || productList.length;
      const productsWithAudio = productList.filter(p => p.has_audio).length;
      
      // Get product status breakdown from sync status
      const activeProducts = syncStatusRes.data.cached_products?.active || 0;
      const draftProducts = syncStatusRes.data.cached_products?.draft || 0;
      const archivedProducts = syncStatusRes.data.cached_products?.archived || 0;
      
      setStats({
        totalProducts,
        productsWithAudio,
        totalAudios: productsWithAudio,
        activeProducts,
        draftProducts,
        archivedProducts
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    setSyncing(true);
    try {
      await apiClient.post('/products/sync');
      toast.success('Product sync started! This may take a few minutes for large catalogs.');
      
      // Poll for sync status
      const pollInterval = setInterval(async () => {
        try {
          const res = await apiClient.get('/products/sync/status');
          setSyncStatus(res.data);
          
          if (res.data.sync_status === 'completed') {
            clearInterval(pollInterval);
            setSyncing(false);
            toast.success(`Sync complete! ${res.data.cached_products?.active || 0} active products loaded.`);
            loadDashboardData();
          } else if (res.data.sync_status === 'failed') {
            clearInterval(pollInterval);
            setSyncing(false);
            toast.error('Sync failed. Please try again.');
          }
        } catch (error) {
          clearInterval(pollInterval);
          setSyncing(false);
        }
      }, 2000);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setSyncing(false);
      }, 300000);
      
    } catch (error) {
      console.error('Error syncing products:', error);
      toast.error('Failed to start product sync');
      setSyncing(false);
    }
  };

  const handlePageChange = (newPage) => {
    loadDashboardData(newPage);
  };

  const handleVoicesUpdate = async () => {
    try {
      const voicesRes = await apiClient.get('/voices');
      setVoices(voicesRes.data.voices || []);
    } catch (error) {
      console.error('Error refreshing voices:', error);
      toast.error('Failed to refresh voices');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-indigo-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-600 p-2 rounded-lg">
                <Volume2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Speakify</h1>
                <p className="text-sm text-gray-600">{shopDomain}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show Billing Plans if requested */}
        {showBilling && (
          <div className="mb-8">
            <BillingPlans 
              currentPlan={subscription?.plan_name || 'free'}
              onPlanChange={() => {
                loadSubscription();
                loadTrialStatus();
                loadDashboardData();
                setShowBilling(false);
              }}
            />
            <div className="mt-4 text-center">
              <Button variant="outline" onClick={() => setShowBilling(false)}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}

        {!showBilling && (
          <>
            {/* Trial Banner */}
            <TrialBanner 
              trialStatus={trialStatus} 
              onUpgradeClick={() => setShowBilling(true)}
            />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Products</CardTitle>
              <Store className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900" data-testid="total-products">
                {stats.totalProducts}
              </div>
              <p className="text-xs text-gray-600">Products in your store</p>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Products with Audio</CardTitle>
              <Music className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900" data-testid="products-with-audio">
                {stats.productsWithAudio}
              </div>
              <p className="text-xs text-gray-600">
                {features.extended_product_limit
                  ? 'Unlimited product limit (Custom Plan)'
                  : stats.totalProducts > 0
                    ? `${Math.round((stats.productsWithAudio / stats.totalProducts) * 100)}% of products`
                    : 'No products yet'
                }
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Available Voices</CardTitle>
              <Volume2 className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900" data-testid="available-voices">
                {voices.length}
              </div>
              <p className="text-xs text-gray-600">Premium AI voices</p>
            </CardContent>
          </Card>

          {/* Subscription Widget */}
          <SubscriptionWidget onUpgradeClick={() => setShowBilling(true)} />
        </div>

        {/* Product Sync Section */}
        <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-indigo-600" />
                Product Catalog Sync
              </CardTitle>
              <CardDescription>
                {syncStatus?.cached_products?.active > 0 
                  ? `${syncStatus.cached_products.active} active products synced`
                  : 'Sync your product catalog to manage audio for all products'}
              </CardDescription>
            </div>
            <Button 
              onClick={handleSyncProducts}
              disabled={syncing}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Products'}
            </Button>
          </CardHeader>
          
          <CardContent className="pt-0 space-y-4">
            {/* Sync Status */}
            {syncStatus && (
              <div className="flex items-center gap-4 text-sm text-gray-600 pb-4 border-b border-gray-200">
                {syncStatus.last_sync && (
                  <span>Last synced: {new Date(syncStatus.last_sync).toLocaleString()}</span>
                )}
                {syncStatus.sync_status === 'in_progress' && (
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                    Sync in progress...
                  </Badge>
                )}
                {syncStatus.sync_status === 'completed' && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    Synced
                  </Badge>
                )}
              </div>
            )}

            {/* How Product Sync Works */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-indigo-100">
              <div className="flex items-start gap-3 mb-3">
                <Info className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">Understanding Product Sync</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Speakify syncs products based on their status in your Shopify store. Here&apos;s your current breakdown:
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-8">
                {/* Active Products */}
                <div className="bg-white rounded-lg p-3 border border-green-200 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="font-semibold text-sm text-gray-900">Active</span>
                    </div>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      {stats.activeProducts}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600">
                    ✅ Synced & ready for audio
                  </p>
                </div>

                {/* Draft Products */}
                <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-600" />
                      <span className="font-semibold text-sm text-gray-900">Draft</span>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                      {stats.draftProducts}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600">
                    ⚠️ Not synced - publish first
                  </p>
                </div>

                {/* Archived Products */}
                <div className="bg-white rounded-lg p-3 border border-gray-300 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Archive className="h-4 w-4 text-gray-600" />
                      <span className="font-semibold text-sm text-gray-900">Archived</span>
                    </div>
                    <Badge className="bg-gray-100 text-gray-800 border-gray-200">
                      {stats.archivedProducts}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600">
                    ❌ Not synced - hidden
                  </p>
                </div>
              </div>

              <div className="mt-3 ml-8 space-y-2">
                <div className="text-xs text-gray-700 bg-white rounded-lg p-3 border border-indigo-100">
                  <strong>💡 Where do I manage these statuses?</strong>
                  <br />
                  Product statuses (Active, Draft, Archived) are managed in your Shopify Admin under Products. 
                  <a 
                    href={`https://${shopDomain}/admin/products`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 font-medium ml-1"
                  >
                    Go to Shopify Products →
                  </a>
                </div>
                
                <div className="text-xs text-gray-700 bg-white rounded-lg p-3 border border-indigo-100">
                  <strong>🔄 When should I sync?</strong>
                  <br />
                  Click &quot;Sync Products&quot; after publishing new products or changing product statuses. 
                  The app also auto-syncs when you add/update products via webhooks.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Embed Onboarding */}
        <div className="mb-8">
          <AppEmbedOnboarding />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <TabsTrigger value="products" data-testid="products-tab">Product Audio</TabsTrigger>
            <TabsTrigger value="voices" data-testid="voices-tab">Voice Settings</TabsTrigger>
            <TabsTrigger value="merchant-experience" data-testid="merchant-experience-tab">Merchant Experience</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Music className="h-5 w-5 text-indigo-600" />
                  <span>Manage Product Audio</span>
                </CardTitle>
                <CardDescription>
                  Add voice descriptions to your products to enhance customer experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ProductAudioManager
                  products={products}
                  voices={voices}
                  onProductUpdate={() => loadDashboardData(pagination.page)}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  trialStatus={trialStatus}
                  extendedProductLimit={features.extended_product_limit ?? false}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="voices" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-indigo-600" />
                  <span>Voice Settings</span>
                </CardTitle>
                <CardDescription>
                  Configure voice parameters and test different voices
                </CardDescription>
              </CardHeader>
              <CardContent>
                <VoiceSettings
                  voices={voices}
                  onVoicesUpdate={handleVoicesUpdate}
                  trialStatus={trialStatus}
                  customVoiceOverride={features.custom_voice_override ?? false}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="merchant-experience" className="space-y-6">
            {/* Merchant Experience Portal Link - Required by Shopify */}
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ExternalLink className="h-5 w-5 text-indigo-600" />
                  <span>Merchant Experience Portal</span>
                </CardTitle>
                <CardDescription>
                  Access your dedicated merchant portal for additional resources
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-2 text-gray-800">🌐 Merchant Experience Portal</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Access tutorials, documentation, and additional merchant resources
                  </p>
                  <Button 
                    onClick={() => window.open('https://speakify-app-1.emergent.host', '_blank')}
                    className="w-full sm:w-auto"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Merchant Portal
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Analytics & ROI Dashboard */}
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  <span>Analytics & ROI Dashboard</span>
                </CardTitle>
                <CardDescription>
                  Track audio performance, customer engagement, and conversion impact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AnalyticsDashboard
                  subscription={subscription}
                  onUpgradeClick={() => setShowBilling(true)}
                  fullAnalyticsOverride={features.full_analytics ?? false}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Getting Started */}
        {stats.productsWithAudio === 0 && stats.totalProducts > 0 && (
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl">🎉 Welcome to SoundProducts!</CardTitle>
              <CardDescription className="text-indigo-100">
                You have {stats.totalProducts} products ready for audio enhancement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-indigo-100 mb-4">
                Get started by adding voice descriptions to your products. This will help customers 
                better understand your products and can increase engagement and conversions.
              </p>
              <Button 
                variant="secondary" 
                className="bg-white text-indigo-600 hover:bg-indigo-50"
                data-testid="get-started-btn"
              >
                <Music className="h-4 w-4 mr-2" />
                Add Your First Audio
              </Button>
            </CardContent>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
